import React from 'react';
import { useForm } from 'react-hook-form';
import NormalInput from '../NormalInput';
import Button from '../../Button';
import Spinner from '../../Spinner/Spinner';

interface ValueValidateInputProps {
  onApply: (value: string) => Promise<boolean>;
  validationError?: string;
  label: string;
  placeholder: string;
  buttonText: string;
  disabled?: boolean;
  pending?: boolean;
  prefillValue?: string;
  /** Shown when the submitted value is empty or too short to be an ID. */
  invalidInputMessage?: string;
}

export function ValueValidateInput({
  onApply,
  validationError,
  label,
  placeholder,
  buttonText,
  disabled = false,
  pending = false,
  prefillValue,
  invalidInputMessage = 'Please enter a valid Smart Lock ID.',
}: Readonly<ValueValidateInputProps>) {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    reset,
    formState: { errors, isValid },
    watch,
  } = useForm<{ input: string }>({ mode: 'onSubmit', defaultValues: { input: prefillValue } });

  // Effect to set validation error from parent
  React.useEffect(() => {
    if (validationError) {
      setError('input', { type: 'manual', message: validationError });
    } else {
      clearErrors('input');
    }
  }, [validationError, setError, clearErrors]);

  const onSubmit = handleSubmit(async ({ input }) => {
    clearErrors();
    if (input && inputValue.length > 3) {
      const applied = await onApply(input);
      if (applied) {
        reset();
      }
    }
  });
  const inputValue = watch('input');

  return (
    <form onSubmit={onSubmit}>
      <div className="flex w-full space-x-2 py-2 ">
        <div className="grow">
          <NormalInput
            label={label}
            name="input"
            // The field box and the submit button share one fixed height, so they
            // stay level in every button state (enabled, disabled, pending).
            wrapperClass="h-12"
            className="h-full w-full rounded-sm px-3"
            register={register}
            placeholder={placeholder}
            registerOptions={{
              required: invalidInputMessage,
              minLength: { value: 4, message: invalidInputMessage },
            }}
          />
        </div>
        <div className="flex items-end">
          <Button
            type="submit"
            className="my-1 flex h-12 items-center justify-center rounded-sm px-8 py-0 text-base"
            disabled={!inputValue || inputValue.length < 3 || disabled || pending}
          >
            {pending ? <Spinner /> : buttonText}
          </Button>
        </div>
      </div>
      <div>{errors && <p className="text-sm font-medium text-red">{errors.input?.message}</p>}</div>
    </form>
  );
}
